/**
 * Pure rules the TV applies to incoming socket frames. Kept out of
 * useScreenSession so they can be unit-tested (see session-frames.test.ts).
 */
import type { LeaderboardState } from '@gymboard/types'

/**
 * Should `next` be dropped because the board already shows a newer frame?
 *
 * Frames can arrive out of order (buffer drain ordering, transport races);
 * applying an older one makes the board jump backward. Order by the
 * server's monotonic `seq` when both frames carry one (clock-skew-immune);
 * fall back to `updatedAt` for frames from before seq existed. Frames for
 * a different session are never considered stale here — routing by
 * session is the caller's job.
 */
export function isStaleLeaderboardFrame(
  prev: LeaderboardState | null,
  next: LeaderboardState | null,
): boolean {
  if (!prev || !next || prev.sessionId !== next.sessionId) return false
  if (typeof next.seq === 'number' && typeof prev.seq === 'number') {
    return next.seq <= prev.seq
  }
  return next.updatedAt <= prev.updatedAt
}

/**
 * Decode the tenantId claim from a ScreenJWT without verifying the signature.
 * The signature is verified server-side on every `session:join:broadcast` —
 * we only need the claim locally to populate the subscribe payload (the
 * server re-reads it from the socket.io auth token via `client.tenantId`
 * anyway, so this is purely for schema symmetry with the user-side join).
 */
export function decodeTenantIdFromScreenToken(token: string | null): string | null {
  if (!token) return null
  try {
    const parts = token.split('.')
    if (parts.length !== 3) return null
    // JWT uses base64url. atob requires standard base64 padding.
    const b64 = parts[1].replace(/-/g, '+').replace(/_/g, '/')
    const pad = b64.length % 4 === 0 ? '' : '='.repeat(4 - (b64.length % 4))
    const json = atob(b64 + pad)
    const claims = JSON.parse(json) as { tenantId?: string }
    return claims.tenantId ?? null
  } catch {
    return null
  }
}
